import { useState } from "react";
import { useScrollReveal } from "../useScrollReveal";

export default function Contact({ data }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [ref, visible] = useScrollReveal();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = () => {
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in your name, email, and message.");
      return;
    }
    setError("");
    setSent(true);
  };

  const inputStyle = {
    background: "var(--off-white)", border: "1px solid var(--border)",
    borderRadius: 8, padding: "0.8rem 1rem", fontSize: "0.9rem",
    fontFamily: "var(--sans)", color: "var(--text-main)",
    outline: "none", width: "100%",
  };

  const contactLinks = [
    { icon: "✉", label: "Email", sub: data.contact.email, href: `mailto:${data.contact.email}` },
    { icon: "in", label: "LinkedIn", sub: data.contact.linkedin, href: "#" },
    { icon: "⌥", label: "GitHub", sub: data.contact.github, href: "#" },
  ];

  return (
    <section id="contact" style={{ padding: "6rem 0" }}>
      <div className="section-inner">
        <div className="section-label">Contact</div>
        <h2 className="section-title">Let's build something <em>great</em></h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4rem", alignItems: "start" }} className="contact-grid">

          {/* Left */}
          <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(24px)", transition: "all 0.6s ease" }}>
            <p style={{ fontSize: "1.05rem", color: "var(--text-secondary)", lineHeight: 1.75, fontWeight: 300, marginBottom: "1.25rem" }}>
              Whether you have a project in mind, want to discuss a role, or just want to talk mobile architecture — I'm always happy to connect.
            </p>
            <p style={{ fontSize: "1.05rem", color: "var(--text-secondary)", lineHeight: 1.75, fontWeight: 300, marginBottom: "2rem" }}>
              I'm currently open to senior engineering roles and select freelance projects.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {contactLinks.map((l) => (
                <a key={l.label} href={l.href} style={{
                  display: "flex", alignItems: "center", gap: "1rem",
                  padding: "1rem 1.25rem", background: "var(--off-white)",
                  border: "1px solid var(--border)", borderRadius: "var(--radius)",
                  color: "var(--text-main)", transition: "background 0.2s",
                }}
                  onMouseOver={e => e.currentTarget.style.background = "var(--light-gray)"}
                  onMouseOut={e => e.currentTarget.style.background = "var(--off-white)"}
                >
                  <div style={{
                    width: 36, height: 36, background: "var(--white)",
                    border: "1px solid var(--border)", borderRadius: 8,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1rem", flexShrink: 0,
                  }}>{l.icon}</div>
                  <div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 500 }}>{l.label}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{l.sub}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              {[
                { label: "Your name", name: "name", placeholder: "Jane Smith", type: "text" },
                { label: "Email address", name: "email", placeholder: "jane@company.com", type: "email" },
              ].map((f) => (
                <div key={f.name} style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                  <label style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>{f.label}</label>
                  <input type={f.type} name={f.name} placeholder={f.placeholder} value={form[f.name]} onChange={handleChange} style={inputStyle} />
                </div>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <label style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Subject</label>
              <input type="text" name="subject" placeholder="Project inquiry / Job opportunity / Just saying hi" value={form.subject} onChange={handleChange} style={inputStyle} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <label style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Message</label>
              <textarea name="message" placeholder="Tell me about your project or idea..." value={form.message} onChange={handleChange} style={{ ...inputStyle, height: 130, resize: "none" }} />
            </div>

            {error && <p style={{ fontSize: "0.85rem", color: "#c0392b" }}>{error}</p>}

            {sent ? (
              <div style={{
                background: "#f0f7ee", border: "1px solid #c8e0c4",
                borderRadius: "var(--radius)", padding: "1rem 1.25rem",
                fontSize: "0.9rem", color: "#3a6e34", textAlign: "center",
              }}>
                ✓ Message sent! I'll get back to you within 1–2 business days.
              </div>
            ) : (
              <button onClick={handleSubmit} style={{
                background: "var(--text-main)", color: "var(--white)",
                border: "none", borderRadius: "999px", padding: "0.9rem 2rem",
                fontSize: "0.9rem", fontWeight: 500, fontFamily: "var(--sans)",
                cursor: "pointer", width: "fit-content", transition: "opacity 0.2s",
              }}
                onMouseOver={e => e.target.style.opacity = "0.75"}
                onMouseOut={e => e.target.style.opacity = "1"}
              >
                Send message
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .contact-grid { grid-template-columns: 1fr 1fr; }
        @media (max-width: 768px) { .contact-grid { grid-template-columns: 1fr !important; gap: 2rem !important; } }
      `}</style>
    </section>
  );
}
